'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useGetServerClockSpec } from '../../generated/specs/system.spec.gen.js';
import { useSdkNetworkScope, useSdkRuntime } from '../../provider/SdkRuntimeContext.js';
import { ServerClockEstimator, sampleServerClock } from '../internal/server-clock-estimator.js';
const REQUEST_DEADLINE_MS = 1_500;
const RETRY_INTERVAL_MS = 5_000;
const RESAMPLE_INTERVAL_MS = 60_000;
const MAX_INITIAL_SAMPLES = 3;
const MAX_USABLE_UNCERTAINTY_MS = 250;
const owners = new WeakMap();
export function useServerClock() {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const networkScope = useSdkNetworkScope();
  const runtime = useSdkRuntime();
  let t0;
  if ($[0] !== networkScope || $[1] !== queryClient) {
    t0 = ownerFor(queryClient, networkScope);
    $[0] = networkScope;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const owner = t0;
  let t1;
  if ($[3] !== owner) {
    t1 = listener => owner.subscribe(listener);
    $[3] = owner;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const subscribe = t1;
  let t2;
  if ($[5] !== owner) {
    t2 = () => owner.getSnapshot();
    $[5] = owner;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getSnapshot = t2;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  let t3;
  let t4;
  if ($[7] !== owner || $[8] !== runtime.adapter) {
    t3 = () => owner.acquire({
      onVisible: listener_0 => runtime.adapter.appState.addListener(event => {
        if (event.kind === "visible" || event.kind === "resume" || event.kind === "bfcache-restore") {
          listener_0();
        }
      }),
      onNetworkChange: listener_1 => runtime.adapter.netInfo.addListener(online => {
        if (online) {
          listener_1();
        }
      })
    });
    t4 = [owner, runtime.adapter];
    $[7] = owner;
    $[8] = runtime.adapter;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  return snapshot;
}
function ownerFor(queryClient, networkScope) {
  let scoped = owners.get(queryClient);
  if (scoped === undefined) {
    scoped = new Map();
    owners.set(queryClient, scoped);
  }
  const existing = scoped.get(networkScope);
  if (existing !== undefined) return existing;
  const owner = createClockOwner(queryClient, networkScope);
  scoped.set(networkScope, owner);
  return owner;
}
function createClockOwner(queryClient, networkScope) {
  const listeners = new Set();
  const estimator = new ServerClockEstimator();
  const clock = {
    estimate: () => estimator.estimate(monotonicNow())
  };
  let sampleGeneration = 0;
  let snapshot = {
    status: 'syncing',
    clock: null,
    reason: null,
    sampleGeneration
  };
  let refs = 0;
  let requestCounter = 0;
  let inFlight = null;
  let trailingRequested = false;
  let timer = null;
  let releaseVisible = null;
  let releaseNetwork = null;
  const publish = next => {
    if (snapshot.status === next.status && snapshot.clock === next.clock && snapshot.reason === next.reason && snapshot.sampleGeneration === next.sampleGeneration) {
      return;
    }
    snapshot = next;
    for (const listener of listeners) listener();
  };
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const schedule = delayMs => {
    clearTimer();
    if (refs === 0) return;
    timer = setTimeout(() => {
      timer = null;
      void synchronize(false);
    }, delayMs);
  };
  const scheduleRecovery = () => {
    clearTimer();
    if (refs === 0) return;
    timer = setTimeout(() => {
      timer = null;
      void synchronize(true);
    }, 0);
  };
  const synchronize = async initial => {
    if (inFlight !== null) {
      trailingRequested = true;
      return inFlight;
    }
    const run = async () => {
      const attempts = initial ? MAX_INITIAL_SAMPLES : 1;
      let best = null;
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        const sample = await requestClockSample(queryClient, networkScope, requestCounter);
        requestCounter += 1;
        if (sample !== null && (best === null || sample.uncertaintyMs < best.uncertaintyMs)) {
          best = sample;
        }
        if (sample !== null && sample.uncertaintyMs <= MAX_USABLE_UNCERTAINTY_MS) break;
      }
      if (best === null || best.uncertaintyMs > MAX_USABLE_UNCERTAINTY_MS) {
        if (snapshot.clock === null || !clock.estimate().usable) {
          publish({
            status: 'unavailable',
            clock: null,
            reason: 'clock_sample_unavailable',
            sampleGeneration
          });
        }
        schedule(RETRY_INTERVAL_MS);
        return;
      }
      const decision = snapshot.clock === null ? 'initialized' : estimator.adopt(best);
      if (decision === 'recovery') {
        publish({
          status: 'unavailable',
          clock: null,
          reason: 'clock_discontinuity',
          sampleGeneration
        });
        scheduleRecovery();
        return;
      }
      if (snapshot.clock === null) estimator.reset(best);
      sampleGeneration += 1;
      publish({
        status: 'ready',
        clock,
        reason: null,
        sampleGeneration
      });
      schedule(RESAMPLE_INTERVAL_MS);
    };
    inFlight = run().finally(() => {
      inFlight = null;
      if (trailingRequested && refs > 0) {
        trailingRequested = false;
        void synchronize(false);
      }
    });
    return inFlight;
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    acquire(lifecycle) {
      refs += 1;
      if (refs === 1) {
        releaseVisible = lifecycle.onVisible(() => void synchronize(false));
        releaseNetwork = lifecycle.onNetworkChange(() => void synchronize(false));
        void synchronize(true);
      }
      return () => {
        refs = Math.max(0, refs - 1);
        if (refs !== 0) return;
        trailingRequested = false;
        clearTimer();
        releaseVisible?.();
        releaseVisible = null;
        releaseNetwork?.();
        releaseNetwork = null;
      };
    }
  };
}
async function requestClockSample(queryClient, networkScope, requestCounter) {
  const detail = useGetServerClockSpec.entityKeys.detail;
  if (detail === undefined) return null;
  const base = detail({});
  const queryKey = ['system', 'clock-sample', networkScope, requestCounter];
  const monotonicSentMs = monotonicNow();
  const deadline = setTimeout(() => {
    // REASON: The deadline cancels only this ephemeral, request-scoped clock sample.
    void queryClient.cancelQueries({
      queryKey,
      exact: true
    }); // ALLOW-STATE-OWNERSHIP: exact transport sample
  }, REQUEST_DEADLINE_MS);
  try {
    const row = await queryClient.fetchQuery({
      ...base,
      queryKey,
      staleTime: 0,
      gcTime: 0,
      retry: false
    });
    const monotonicReceivedMs = monotonicNow();
    if (row === null) return null;
    return sampleServerClock({
      monotonicSentMs,
      monotonicReceivedMs,
      serverReceivedAtMs: row.serverReceivedAtMs,
      serverSentAtMs: row.serverSentAtMs
    });
  } catch {
    return null;
  } finally {
    clearTimeout(deadline);
    // REASON: Clock samples are nonpersistent transport data and must be discarded after use.
    queryClient.removeQueries({
      queryKey,
      exact: true
    }); // ALLOW-STATE-OWNERSHIP: exact transport sample cleanup
  }
}
function monotonicNow() {
  return globalThis.performance.now();
}